import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';

/**
 * Data constants for income statement categories
 */
const incomeList = [
    "Basic Contractual Income",
    "Rent Turnover Income",
    "Insurance Income - Revenue",
    "Covid Credits - Rental",
    "Other Income",
    "Other Contractual Income",
    "Promotional Income",
];

const netMunicipalsList = [
    "Rates Income",
    "Electricity Recovered",
    "Water Recovered",
    "Sewerage Recovered ",
    "Refuse Recovered",
    "Rates Expense",
    "Electricity Expense",
    "Water Consumption Expense",
    "Sewerage Expense",
    "Refuse Expense"
];

const opExList = [
    "Bank Charges",
    "Cleaning",
    "Insurance",
    "Legal Fees",
    "Management Fee",
    "Marketing",
    "Other Operating Expenses",
    "Repairs & Maintenance",
    "Sanitation & Pest Control",
    "Security",
];

const fuelExpenseList = [
    "Fuel"
];

const nonOpExList = [
    "Audit Secretarial Fees",
    "Professional Fees",
    "SRI Expenses",
    "Depreciation"
];

const netInterestExpenseList = [
    "Interest Received",
    "Interest Paid",
    "Interest Paid InterCo"
];

const revalTaxList = [
    "Revaluation",
    "Profit/ Loss associate",
    "Taxation"
];

const badDebtsList = [
    "Provision for Bad Debts",
    "Bad debts written off",
    "Bad Debts Recovered" 
];

const lettingCostList = [
    "Tenant Installations",
    "Consolidation Costs",
    "Letting Commission"
];

/**
 * CondensedIncomeStatementTable Component
 * 
 * A reusable table component that displays condensed income statement data
 * including income, expenses, and various financial metrics with tooltips
 * showing detailed breakdowns.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.condensedIncomeData - Income statement data array
 * @param {number|string} props.period - Financial period (e.g., 202507)
 * @param {number} props.totalArea - Total GLA in square meters
 * @param {Object} props.propertyData - Property information object
 * @returns {JSX.Element} Rendered income statement table
 */
const CondensedIncomeStatementTable = ({ condensedIncomeData, period, totalArea, propertyData }) => {
    
    const getMonthYear = (period) => {
        if (!period) return { monthName: '', year: '', prevYear: '' };

        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];

        const periodStr = period.toString().padStart(6, '0');
        const year = periodStr.substring(0, 4);
        const monthIndex = parseInt(periodStr.substring(4, 6), 10) - 1;
        const prevYear = (parseInt(year) - 1).toString();

        return {
            monthName: monthNames[monthIndex],
            year,
            prevYear
        };
    };

    const { monthName, year } = getMonthYear(period);

    /**
     * Parses a formatted income value string back to a number.
     * Handles various locale formats:
     * - en-ZA: "1 234,56" (space thousands separator, comma decimal)
     * - en-US: "1,234.56" (comma thousands separator, period decimal)
     * - fr-FR: "1 234,56" (space thousands separator, comma decimal)
     * 
     * @param {number|string} val - The value to parse
     * @returns {number} The parsed numeric value
     */
    const parseIncomeValue = (val) => {
        if (typeof val === "number") return val;
        if (typeof val === "string") {
            // Handle "-" or empty strings
            if (val === "-" || val.trim() === "") return 0;
            
            // Remove thousands separators (spaces, apostrophes, and commas when used as thousands separators)
            // Then normalize decimal separator to period
            let cleaned = val.replace(/[\s']/g, ''); // Remove spaces and apostrophes
            
            // Determine if comma is decimal separator or thousands separator
            // If there's a period after the comma, comma is thousands separator
            // If comma is the last punctuation, it's likely a decimal separator
            const lastCommaIndex = cleaned.lastIndexOf(',');
            const lastPeriodIndex = cleaned.lastIndexOf('.');
            
            if (lastCommaIndex > lastPeriodIndex) {
                // Comma appears after period, or no period exists, so comma is decimal separator
                // Remove any commas used as thousands separators (before the last comma)
                // and convert the last comma to a period
                cleaned = cleaned.substring(0, lastCommaIndex).replace(/,/g, '') + 
                          '.' + 
                          cleaned.substring(lastCommaIndex + 1);
            } else {
                // Period appears after comma (or at same position), so comma is thousands separator
                cleaned = cleaned.replace(/,/g, '');
            }
            
            const num = parseFloat(cleaned);
            return isNaN(num) ? 0 : num;
        }
        return 0;
    };

    const formatNumberWithSpace = (value) => {
        if (typeof value !== 'number') return value;
        let formatted = value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
        return formatted.replace(/\u202F/g, '  ');
    };

    /**
     * Calculates the percentage variance between actual and budget values.
     * Uses parseIncomeValue to correctly handle locale-specific formatting
     * (thousands separators, decimal points, etc.)
     */
    const calculateVariance = (actual, budget) => {
        // If either value is explicitly marked as not available ("-"), we cannot compute a meaningful variance.
        if (actual === "-" || budget === "-") {
            return "-";
        }

        // Use the shared income parsing helper to correctly handle:
        // - Locale-specific thousands separators (spaces, commas, apostrophes)
        // - Placeholder values like "-" or empty strings
        // - Both numeric and string inputs
        const actualValue = parseIncomeValue(actual);
        const budgetValue = parseIncomeValue(budget);

        // Avoid division by zero or invalid numeric values when calculating the percentage variance.
        if (!isFinite(budgetValue) || budgetValue === 0) {
            return "-";
        }

        const variance = ((actualValue - budgetValue) / budgetValue) * 100;
        return variance.toFixed(1) + "%";
    };

    const sumCategoryData = (categoryList, valueField) => {
        if (!condensedIncomeData) {
            return 0;
        }

        const items = condensedIncomeData.filter(item =>
            categoryList.includes(item.Caption)
        );

        if (items.length === 0) {
            return 0;
        }

        return items.reduce((sum, item) => {
            const value = typeof item[valueField] === 'number'
                ? item[valueField]
                : parseFloat(item[valueField] || '0');
            return sum + (value * -1);
        }, 0);
    };

    const calculateIncome = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const incomeItems = condensedIncomeData.filter(item =>
            incomeList.includes(item.Caption)
        );

        if (incomeItems.length > 0) {
            const total = incomeItems.reduce((sum, item) => {
                const value = typeof item[valueField] === 'number'
                    ? item[valueField]
                    : parseFloat(item[valueField] || '0');
                return sum + (value * -1);
            }, 0);

            const inMillions = total / 1000000;
            return inMillions.toLocaleString('en-ZA', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        }

        return "0.0";
    };

    const calculateNetMunicipals = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        // Note: Checks both Caption and MdaAccountName to handle data variations in API response
        const municipalItems = condensedIncomeData.filter(item =>
            netMunicipalsList.includes(item.Caption) || netMunicipalsList.includes(item.MdaAccountName)
        );

        if (municipalItems.length > 0) {
            const total = municipalItems.reduce((sum, item) => {
                const value = typeof item[valueField] === 'number'
                    ? item[valueField]
                    : parseFloat(item[valueField] || '0');
                return sum + (value * -1);
            }, 0);

            const inMillions = total / 1000000;
            return inMillions.toLocaleString('en-ZA', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        }
        return "0.0";
    };

    const calculateOperatingEx = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const sumList = (list) => {
            return condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((sum, item) => {
                    const value = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (value * -1);
                }, 0);
        };

        const opEx = sumList(opExList);
        const badDebts = sumList(badDebtsList);
        const lettingCost = sumList(lettingCostList);

        const total = opEx + badDebts + lettingCost;
        const inMillions = total / 1000000;

        return inMillions.toLocaleString('en-ZA', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        });
    };

    const calculateFuel = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const fuelItems = condensedIncomeData.filter(item =>
            fuelExpenseList.includes(item.Caption)
        );

        if (fuelItems.length > 0) {
            const total = fuelItems.reduce((sum, item) => {
                const value = typeof item[valueField] === 'number'
                    ? item[valueField]
                    : parseFloat(item[valueField] || '0');
                return sum + (value * -1);
            }, 0);

            const inMillions = total / 1000000;
            return inMillions.toLocaleString('en-ZA', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        }
        return "0.0";
    };

    const calculateNonOperatingEx = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const nonOpExItems = condensedIncomeData.filter(item =>
            nonOpExList.includes(item.Caption)
        );

        if (nonOpExItems.length > 0) {
            const total = nonOpExItems.reduce((sum, item) => {
                const value = typeof item[valueField] === 'number'
                    ? item[valueField]
                    : parseFloat(item[valueField] || '0');
                return sum + (value * -1);
            }, 0);

            const inMillions = total / 1000000;
            return inMillions.toLocaleString('en-ZA', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        }
        return "0.0";
    };

    const calculateNetOperatingIncome = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const incomeSum = sumCategoryData(incomeList, valueField);
        const municipalsSum = sumCategoryData(netMunicipalsList, valueField);
        const opExSum = sumCategoryData(opExList, valueField);
        const fuelSum = sumCategoryData(fuelExpenseList, valueField);
        const badDebts = sumCategoryData(badDebtsList, valueField);
        const lettingCosts = sumCategoryData(lettingCostList, valueField);

        const total = incomeSum + municipalsSum + opExSum + fuelSum + badDebts + lettingCosts;

        const inMillions = total / 1000000;
        return inMillions.toLocaleString('en-ZA', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        });
    };

    const calculateNetOperatingIncomeBeforeFinance = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const netOperatingIncomeSum = sumCategoryData(incomeList, valueField) +
        sumCategoryData(netMunicipalsList, valueField) +
        sumCategoryData(opExList, valueField) + 
        sumCategoryData(fuelExpenseList, valueField) +
        sumCategoryData(badDebtsList, valueField) + 
        sumCategoryData(lettingCostList, valueField);

        const nonOpExSum = sumCategoryData(nonOpExList, valueField);

        const total = netOperatingIncomeSum + nonOpExSum;

        const inMillions = total / 1000000;
        return inMillions.toLocaleString('en-ZA', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        });
    };

    const calculateNetInterestEx = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const netInterestItems = condensedIncomeData.filter(item =>
            netInterestExpenseList.includes(item.Caption)
        );

        if (netInterestItems.length > 0) {
            const total = netInterestItems.reduce((sum, item) => {
                const value = typeof item[valueField] === 'number'
                    ? item[valueField]
                    : parseFloat(item[valueField] || '0');
                return sum + (value * -1);
            }, 0);

            const inMillions = total / 1000000;
            return inMillions.toLocaleString('en-ZA', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        }
        return "0.0";
    };

    const calculateRevalAndTax = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const revalAndTaxItems = condensedIncomeData.filter(item =>
            revalTaxList.includes(item.Caption)
        );

        if (revalAndTaxItems.length > 0) {
            const total = revalAndTaxItems.reduce((sum, item) => {
                const value = typeof item[valueField] === 'number'
                    ? item[valueField]
                    : parseFloat(item[valueField] || '0');
                return sum + (value * -1);
            }, 0);

            const inMillions = total / 1000000;
            return inMillions.toLocaleString('en-ZA', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        }
        return "0.0";
    };

    const calculateNetIncome = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const netOperatingIncome = sumCategoryData(incomeList, valueField) +
            sumCategoryData(netMunicipalsList, valueField) +
            sumCategoryData(opExList, valueField) +
            sumCategoryData(fuelExpenseList, valueField) +
            sumCategoryData(badDebtsList, valueField) +
            sumCategoryData(lettingCostList, valueField);

        const nonOpEx = sumCategoryData(nonOpExList, valueField);
        const interest = sumCategoryData(netInterestExpenseList, valueField);
        const revalTax = sumCategoryData(revalTaxList, valueField);

        const total = netOperatingIncome + nonOpEx + interest + revalTax;

        const inMillions = total / 1000000;
        return inMillions.toLocaleString('en-ZA', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        });
    };

    const getVarianceTooltipHtml = (actual, budget, labelActual = "Actual YTD", labelBudget = "Budget YTD") => {
        return `
        <table>
            <thead>
                <tr>
                    <th>Value</th>
                    <th>Amount (R'm)</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>${labelActual}</td>
                    <td>${actual}</td>
                </tr>
                <tr>
                    <td>${labelBudget}</td>
                    <td>${budget}</td>
                </tr>
            </tbody>
        </table>
    `;
    };

    const getIncomeTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        html += incomeList.map(caption => {
            const items = condensedIncomeData.filter(
                i => i.Caption === caption || i.MdaAccountName === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        html += '</tbody></table>';
        return html;
    };

    const getNetMunicipalsTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        html += netMunicipalsList.map(caption => {
            const items = condensedIncomeData.filter(
                i => i.Caption === caption || i.MdaAccountName === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        html += '</tbody></table>';
        return html;
    };

    const getOperatingExTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';

        const sumAndFormat = (list) => {
            const sum = condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((acc, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return acc + (v * -1);
                }, 0);
            const inMillions = sum / 1000000;
            return {
                sum,
                formatted: inMillions.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).replace(/\u202F/g, '  ')
            };
        };

        const opEx = sumAndFormat(opExList);
        const badDebts = sumAndFormat(badDebtsList);
        const lettingCost = sumAndFormat(lettingCostList);

        const total = opEx.sum + badDebts.sum + lettingCost.sum;
        const totalFormatted = (total / 1000000).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).replace(/\u202F/g, '  ');

        let html = `<table>
        <thead>
            <tr>
                <th>Component</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>
            <tr><td>Operating expenses (excl. fuel)</td><td>${opEx.formatted}</td></tr>
            <tr><td>Bad debts</td><td>${badDebts.formatted}</td></tr>
            <tr><td>Letting cost</td><td>${lettingCost.formatted}</td></tr>
            <tr style="font-weight:bold;"><td>Total</td><td>${totalFormatted}</td></tr>
        </tbody>
    </table>`;
        return html;
    };

    const getFuelTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        html += fuelExpenseList.map(caption => {
            const items = condensedIncomeData.filter(
                i => i.Caption === caption || i.MdaAccountName === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        html += '</tbody></table>';
        return html;
    };

    const getNonOperatingExTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        let total = 0;
        html += nonOpExList.map(caption => {
            const items = condensedIncomeData.filter(
                i => i.Caption === caption || i.MdaAccountName === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            total += value;
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        const totalFormatted = (total / 1000000).toLocaleString('fr-FR', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }).replace(/\u202F/g, '  ');
        html += `<tr style="font-weight:bold;">
        <td>Total</td>
        <td>${totalFormatted}</td>
    </tr>`;
        html += '</tbody></table>';
        return html;
    };

    const getNetOperatingIncomeTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
    <thead>
        <tr>
            <th>Component</th>
            <th>Amount (R'm)</th>
        </tr>
    </thead>
    <tbody>`;

        const sumAndFormat = (list) => {
            const sum = condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((acc, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return acc + (v * -1);
                }, 0);
            const inMillions = sum / 1000000;
            return { sum, formatted: inMillions.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) };
        };

        const income = sumAndFormat(incomeList);
        const municipals = sumAndFormat(netMunicipalsList);
        const opEx = sumAndFormat(opExList);
        const fuel = sumAndFormat(fuelExpenseList);
        const badDebts = sumAndFormat(badDebtsList);
        const lettingCost = sumAndFormat(lettingCostList);

        html += `<tr><td>Income</td><td>${income.formatted}</td></tr>`;
        html += `<tr><td>Net municipals</td><td>${municipals.formatted}</td></tr>`;
        html += `<tr><td>Operating expenses (excl. fuel)</td><td>${opEx.formatted}</td></tr>`;
        html += `<tr><td>Fuel expense</td><td>${fuel.formatted}</td></tr>`;
        html += `<tr><td>Bad debts</td><td>${badDebts.formatted}</td></tr>`;
        html += `<tr><td>Letting cost</td><td>${lettingCost.formatted}</td></tr>`;

        const total = income.sum + municipals.sum + opEx.sum + fuel.sum + badDebts.sum + lettingCost.sum;
        const totalFormatted = (total / 1000000).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

        html += `<tr style="font-weight:bold;"><td>Total</td><td>${totalFormatted}</td></tr>`;
        html += '</tbody></table>';
        return html;
    };

    const getNetOperatingIncomeBeforeFinanceTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
    <thead>
        <tr>
            <th>Component</th>
            <th>Amount (R'm)</th>
        </tr>
    </thead>
    <tbody>`;

        const sumAndFormat = (list) => {
            const sum = condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((acc, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return acc + (v * -1);
                }, 0);
            const inMillions = sum / 1000000;
            return { sum, formatted: inMillions.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) };
        };

        const income = sumAndFormat(incomeList);
        const municipals = sumAndFormat(netMunicipalsList);
        const opEx = sumAndFormat(opExList);
        const fuel = sumAndFormat(fuelExpenseList);
        const badDebts = sumAndFormat(badDebtsList);
        const lettingCost = sumAndFormat(lettingCostList);
        const nonOpEx = sumAndFormat(nonOpExList);

        html += `<tr><td>Income</td><td>${income.formatted}</td></tr>`;
        html += `<tr><td>Net municipals</td><td>${municipals.formatted}</td></tr>`;
        html += `<tr><td>Operating expenses (excl. fuel)</td><td>${opEx.formatted}</td></tr>`;
        html += `<tr><td>Fuel expense</td><td>${fuel.formatted}</td></tr>`;
        html += `<tr><td>Bad debts</td><td>${badDebts.formatted}</td></tr>`;
        html += `<tr><td>Letting cost</td><td>${lettingCost.formatted}</td></tr>`;
        html += `<tr><td>Non operating expenses</td><td>${nonOpEx.formatted}</td></tr>`;

        const total = income.sum + municipals.sum + opEx.sum + fuel.sum + badDebts.sum + lettingCost.sum + nonOpEx.sum;
        const totalFormatted = (total / 1000000).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

        html += `<tr style="font-weight:bold;"><td>Total</td><td>${totalFormatted}</td></tr>`;
        html += '</tbody></table>';
        return html;
    };

    const getNetInterestExTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        let total = 0;
        html += netInterestExpenseList.map(caption => {
            const items = condensedIncomeData.filter(
                i => i.Caption === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            total += value;
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        const totalFormatted = (total / 1000000).toLocaleString('fr-FR', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }).replace(/\u202F/g, '  ');
        html += `<tr style="font-weight:bold;">
        <td>Total</td>
        <td>${totalFormatted}</td>
    </tr>`;
        html += '</tbody></table>';
        return html;
    };

    const getRevalAndTaxTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        html += revalTaxList.map(caption => {
            const items = condensedIncomeData.filter(
                i => i.Caption === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        html += '</tbody></table>';
        return html;
    };

    const getNetIncomeTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
    <thead>
        <tr>
            <th>Component</th>
            <th>Amount (R'm)</th>
        </tr>
    </thead>
    <tbody>`;

        const sumAndFormat = (list) => {
            const sum = condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((acc, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return acc + (v * -1);
                }, 0);
            const inMillions = sum / 1000000;
            return { sum, formatted: inMillions.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) };
        };

        const income = sumAndFormat(incomeList);
        const municipals = sumAndFormat(netMunicipalsList);
        const opEx = sumAndFormat(opExList);
        const fuel = sumAndFormat(fuelExpenseList);
        const badDebts = sumAndFormat(badDebtsList);
        const lettingCost = sumAndFormat(lettingCostList);
        const nonOpEx = sumAndFormat(nonOpExList);
        const interest = sumAndFormat(netInterestExpenseList);
        const revalTax = sumAndFormat(revalTaxList);

        html += `<tr><td>Income</td><td>${income.formatted}</td></tr>`;
        html += `<tr><td>Net municipals</td><td>${municipals.formatted}</td></tr>`;
        html += `<tr><td>Operating expenses (excl. fuel)</td><td>${opEx.formatted}</td></tr>`;
        html += `<tr><td>Fuel expense</td><td>${fuel.formatted}</td></tr>`;
        html += `<tr><td>Bad debts</td><td>${badDebts.formatted}</td></tr>`;
        html += `<tr><td>Letting cost</td><td>${lettingCost.formatted}</td></tr>`;
        html += `<tr><td>Non operating expenses</td><td>${nonOpEx.formatted}</td></tr>`;
        html += `<tr><td>Net interest expense</td><td>${interest.formatted}</td></tr>`;
        html += `<tr><td>Revaluation and tax</td><td>${revalTax.formatted}</td></tr>`;

        const total = income.sum + municipals.sum + opEx.sum + fuel.sum + badDebts.sum + lettingCost.sum + nonOpEx.sum + interest.sum + revalTax.sum;
        const totalFormatted = (total / 1000000).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

        html += `<tr style="font-weight:bold;"><td>Total</td><td>${totalFormatted}</td></tr>`;
        html += '</tbody></table>';
        return html;
    };

    const getGLATooltipHtml = () => {
        if (!propertyData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Property</th>
                <th>GLA (m²)</th>
            </tr>
        </thead>
        <tbody>`;
        html += Object.entries(propertyData)
            .filter(([name]) => name !== "Unity Centre")
            .map(([name, area]) => {
                const formatted = Number(area)
                    .toLocaleString('fr-FR', { maximumFractionDigits: 0 })
                    .replace(/\u202F/g, '  ');
                return `<tr>
                <td>${name}</td>
                <td>${formatted}</td>
            </tr>`;
            })
            .join('');
        html += '</tbody></table>';
        return html;
    };

    const formatOrDash = (value) => {
        if (value === "0.0" || value === "0,0") return "-";
        return value;
    };

    if (!condensedIncomeData || !period) {
        return null;
    }

    return (
        <div className="income-statement-container">
            <table className="income-statement-table">
                <thead>
                    <tr>
                        <th>Condensed Income Statement</th>
                        <th>{`Actual Month to Date ${monthName} ${year} (R'm)`}</th>
                        <th className="padding-col">{`Budget Month to Date ${monthName} ${year} (R'm)`}</th>
                        <th style={{ borderRight: '2px solid #57565B' }}>% Variance</th>
                        <th>{`Actual Year to Date ${monthName} ${year} (R'm)`}</th>
                        <th className="padding-col">{`Budget Year to Date ${monthName} ${year} (R'm)`}</th>
                        <th style={{ borderRight: '2px solid #57565B' }}>% Variance</th>
                        <th>{`Forecast Year Ended December ${year} (R'm)`}</th>
                        <th className="padding-col">{`Budget Year Ended December ${year} (R'm)`}</th>
                        <th>% Variance</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>GLA (m²)</td>
                        <td data-tooltip-id="tt"
                            data-tooltip-content={getGLATooltipHtml()}
                        >
                            {formatNumberWithSpace(totalArea)}
                        </td>
                        <td className="padding-col">-</td>
                        <td style={{ borderRight: '2px solid #57565B' }}>-</td>
                        <td data-tooltip-id="tt"
                            data-tooltip-content={getGLATooltipHtml()}>{formatNumberWithSpace(totalArea)}</td>
                        <td className="padding-col">-</td>
                        <td style={{ borderRight: '2px solid #57565B' }}>-</td>
                        <td>-</td>
                                <td className="padding-col">-</td>
                        <td>-</td>
                    </tr>
                    <tr>
                        <td>Income</td>
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getIncomeTooltipHtml('CurrentMovements')}
                                >{calculateIncome('CurrentMovements')}</td>
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getIncomeTooltipHtml('BudgetAmount')}
                                >{calculateIncome('BudgetAmount')}</td>
                                {(() => {
                                    const actual = calculateIncome('CurrentMovements');
                                    const budget = calculateIncome('BudgetAmount');
                                    return (
                                        <td style={{ borderRight: '2px solid #57565B' }}
                                            data-tooltip-id="tt"
                                            data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual Month", "Budget Month")}
                                        >
                                            {calculateVariance(actual, budget)}
                                        </td>
                                    );
                                })()}
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getIncomeTooltipHtml('CurrentBal')}
                                >{calculateIncome('CurrentBal')}</td>
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getIncomeTooltipHtml('BudgetYearToDate')}        
                                >{calculateIncome('BudgetYearToDate')}</td>
                                {(() => {
                                    const actual = calculateIncome('CurrentBal');
                                    const budget = calculateIncome('BudgetYearToDate');
                                    return (
                                        <td style={{ borderRight: '2px solid #57565B' }}
                                            data-tooltip-id="tt"
                                            data-tooltip-content={getVarianceTooltipHtml(actual, budget)}
                                        >
                                            {calculateVariance(actual, budget)}
                                        </td>
                                    );
                                })()}
                                {(() => {
                                    const result = parseIncomeValue(calculateIncome('BudgetFullYearRevised'));
                                    return (
                                        <td
                                            data-tooltip-id="tt"
                                            data-tooltip-content={getIncomeTooltipHtml('BudgetFullYearRevised')}
                                        >
                                            {result.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                        </td>
                                    );
                                })()}
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getIncomeTooltipHtml('BudgetFullYear')}
                                    >{calculateIncome('BudgetFullYear')}</td>
                                {(() => {
                                    const forecast = parseIncomeValue(calculateIncome('BudgetFullYearRevised'));
                                    const forecastStr = forecast.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                                    const budgetStr = calculateIncome('BudgetFullYear');
                                    const tooltipHtml = `
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Component</th>
                                                    <th>Amount (R'm)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr style="font-weight:bold;"><td>Forecast Total</td><td>${forecastStr}</td></tr>
                                                <tr style="font-weight:bold;"><td>Budget Full Year</td><td>${budgetStr}</td></tr>
                                            </tbody>
                                        </table>
                                    `;
                                    return (
                                        <td
                                            data-tooltip-id="tt"
                                            data-tooltip-content={tooltipHtml}
                                        >
                                            {calculateVariance(forecastStr, budgetStr)}
                                        </td>
                                    );
                                })()}
                    </tr>
                    <tr>
                        <td>Net municipals</td>
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetMunicipalsTooltipHtml('CurrentMovements')}
                                >{calculateNetMunicipals('CurrentMovements')}</td>
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetMunicipalsTooltipHtml('BudgetAmount')}
                                >{calculateNetMunicipals('BudgetAmount')}</td>
                                {(() => {
                                    const actual = calculateNetMunicipals('CurrentMovements');
                                    const budget = calculateNetMunicipals('BudgetAmount');
                                    return (
                                        <td style={{ borderRight: '2px solid #57565B' }}
                                            data-tooltip-id="tt"
                                            data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual Month", "Budget Month")}
                                        >
                                            {calculateVariance(actual, budget)}
                                        </td>
                                    );
                                })()}
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetMunicipalsTooltipHtml('CurrentBal')}
                                >{calculateNetMunicipals('CurrentBal')}</td>
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetMunicipalsTooltipHtml('BudgetYearToDate')}
                                >{calculateNetMunicipals('BudgetYearToDate')}</td>
                                {(() => {
                                    const actual = calculateNetMunicipals('CurrentBal');
                                    const budget = calculateNetMunicipals('BudgetYearToDate');
                                    return (
                                        <td style={{ borderRight: '2px solid #57565B' }}
                                            data-tooltip-id="tt"
                                            data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual YTD", "Budget YTD")}
                                        >
                                            {calculateVariance(actual, budget)}
                                        </td>
                                    );
                                })()}
                                {(() => {
                                    const result = parseIncomeValue(calculateNetMunicipals('BudgetFullYearRevised'));
                                    return (
                                        <td
                                            data-tooltip-id="tt"
                                            data-tooltip-content={getNetMunicipalsTooltipHtml('BudgetFullYearRevised')}
                                        >
                                            {result.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                        </td>
                                    );
                                })()}
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetMunicipalsTooltipHtml('BudgetFullYear')}
                                >{calculateNetMunicipals('BudgetFullYear')}</td>
                                {(() => {
                                    const forecast = parseIncomeValue(calculateNetMunicipals('BudgetFullYearRevised'));
                                    const forecastStr = forecast.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                                    const budgetStr = calculateNetMunicipals('BudgetFullYear');
                                    const tooltipHtml = `
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Component</th>
                                                    <th>Amount (R'm)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr style="font-weight:bold;"><td>Forecast Total</td><td>${forecastStr}</td></tr>
                                                <tr style="font-weight:bold;"><td>Budget Full Year</td><td>${budgetStr}</td></tr>
                                            </tbody>
                                        </table>
                                    `;
                                    return (
                                        <td
                                            data-tooltip-id="tt"
                                            data-tooltip-content={tooltipHtml}
                                        >
                                            {calculateVariance(forecastStr, budgetStr)}
                                        </td>
                                    );
                                })()}
                    </tr>
                    <tr>
                        <td>Operating expenses (excl. fuel)</td>
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getOperatingExTooltipHtml('CurrentMovements')}
                                >
                                    {calculateOperatingEx('CurrentMovements')}
                                </td>
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getOperatingExTooltipHtml('BudgetAmount')}
                                >{calculateOperatingEx('BudgetAmount')}</td>
                                {(() => {
                                    const actual = calculateOperatingEx('CurrentMovements');
                                    const budget = calculateOperatingEx('BudgetAmount');
                                    return (
                                        <td style={{ borderRight: '2px solid #57565B' }}
                                            data-tooltip-id="tt"
                                            data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual Month", "Budget Month")}
                                        >
                                            {calculateVariance(actual, budget)}
                                        </td>
                                    );
                                })()}
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getOperatingExTooltipHtml('CurrentBal')}
                                >{calculateOperatingEx('CurrentBal')}</td>
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getOperatingExTooltipHtml('BudgetYearToDate')}
                                >{calculateOperatingEx('BudgetYearToDate')}</td>
                                {(() => {
                                    const actual = calculateOperatingEx('CurrentBal');
                                    const budget = calculateOperatingEx('BudgetYearToDate');
                                    return (
                                        <td style={{ borderRight: '2px solid #57565B' }}
                                            data-tooltip-id="tt"
                                            data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual YTD", "Budget YTD")}
                                        >
                                            {calculateVariance(actual, budget)}
                                        </td>
                                    );
                                })()}
                                {(() => {
                                    const result = parseIncomeValue(calculateOperatingEx('BudgetFullYearRevised'));
                                    return (
                                        <td
                                            data-tooltip-id="tt"
                                            data-tooltip-content={getOperatingExTooltipHtml('BudgetFullYearRevised')}
                                        >
                                            {result.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                        </td>
                                    );
                                })()}
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getOperatingExTooltipHtml('BudgetFullYear')}
                                >{calculateOperatingEx('BudgetFullYear')}</td>
                                {(() => {
                                    const forecast = parseIncomeValue(calculateOperatingEx('BudgetFullYearRevised'));
                                    const forecastStr = forecast.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                                    const budgetStr = calculateOperatingEx('BudgetFullYear');
                                    const tooltipHtml = `
                                    <table>
                                      <thead>
                                        <tr>
                                          <th>Component</th>
                                          <th>Amount (R'm)</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr style="font-weight:bold;"><td>Forecast Total</td><td>${forecastStr}</td></tr>
                                        <tr style="font-weight:bold;"><td>Budget Full Year</td><td>${budgetStr}</td></tr>
                                      </tbody>
                                    </table>
                                  `;
                                    return (
                                        <td
                                            data-tooltip-id="tt"
                                            data-tooltip-content={tooltipHtml}
                                        >
                                            {calculateVariance(forecastStr, budgetStr)}
                                        </td>
                                    );
                                })()}
                    </tr>
                    <tr>
                        <td>Fuel expense</td>
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getFuelTooltipHtml('CurrentMovements')}
                                >{calculateFuel('CurrentMovements')}</td>
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getFuelTooltipHtml('BudgetAmount')}
                                >{calculateFuel('BudgetAmount')}</td>
                                <td style={{ borderRight: '2px solid #57565B' }}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getVarianceTooltipHtml(
                                        calculateFuel('CurrentMovements'),
                                        calculateFuel('BudgetAmount'),
                                        "Actual Month",
                                        "Budget Month"
                                    )}
                                >
                                    {calculateVariance(calculateFuel('CurrentMovements'), calculateFuel('BudgetAmount'))}
                                </td>
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getFuelTooltipHtml('CurrentBal')}
                                >{calculateFuel('CurrentBal')}</td>
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getFuelTooltipHtml('BudgetYearToDate')}
                                >{calculateFuel('BudgetYearToDate')}</td>
                                <td style={{ borderRight: '2px solid #57565B' }}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getVarianceTooltipHtml(
                                        calculateFuel('CurrentBal'),
                                        calculateFuel('BudgetYearToDate'),
                                        "Actual YTD",
                                        "Budget YTD"
                                    )}
                                >
                                    {calculateVariance(calculateFuel('CurrentBal'), calculateFuel('BudgetYearToDate'))}
                                </td>
                                {(() => {
                                    const result = parseIncomeValue(calculateFuel('BudgetFullYearRevised'));
                                    return (
                                        <td
                                            data-tooltip-id="tt"
                                            data-tooltip-content={getFuelTooltipHtml('BudgetFullYearRevised')}
                                        >
                                            {result.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                        </td>
                                    );
                                })()}
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getFuelTooltipHtml('BudgetFullYear')}
                                >{calculateFuel('BudgetFullYear')}</td>
                                {(() => {
                                    const forecast = parseIncomeValue(calculateFuel('BudgetFullYearRevised'));
                                    const forecastStr = forecast.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                                    const budgetStr = calculateFuel('BudgetFullYear');
                                    const tooltipHtml = `
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Component</th>
                                                    <th>Amount (R'm)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr style="font-weight:bold;"><td>Forecast Total</td><td>${forecastStr}</td></tr>
                                                <tr style="font-weight:bold;"><td>Budget Full Year</td><td>${budgetStr}</td></tr>
                                            </tbody>
                                        </table>
                                    `;
                                    return (
                                        <td
                                            data-tooltip-id="tt"
                                            data-tooltip-content={tooltipHtml}
                                        >
                                            {calculateVariance(forecastStr, budgetStr)}
                                        </td>
                                    );
                                })()}
                    </tr>
                            <tr className="subtotal-row">
                        <td>Net operating income</td>
                        <td
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetOperatingIncomeTooltipHtml('CurrentMovements')}
                        >
                            {calculateNetOperatingIncome('CurrentMovements')}
                        </td>
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetOperatingIncomeTooltipHtml('BudgetAmount')}
                        >
                            {calculateNetOperatingIncome('BudgetAmount')}
                        </td>
                        {(() => {
                            const actual = calculateNetOperatingIncome('CurrentMovements');
                            const budget = calculateNetOperatingIncome('BudgetAmount');
                            return (
                                <td style={{ borderRight: '2px solid #57565B' }}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual Month", "Budget Month")}
                                >
                                    {calculateVariance(actual, budget)}
                                </td>
                            );
                        })()}
                        <td
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetOperatingIncomeTooltipHtml('CurrentBal')}
                        >
                            {calculateNetOperatingIncome('CurrentBal')}
                        </td>
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetOperatingIncomeTooltipHtml('BudgetYearToDate')}
                        >
                            {calculateNetOperatingIncome('BudgetYearToDate')}
                        </td>
                        {(() => {
                            const actual = calculateNetOperatingIncome('CurrentBal');
                            const budget = calculateNetOperatingIncome('BudgetYearToDate');
                            return (
                                <td style={{ borderRight: '2px solid #57565B' }}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual YTD", "Budget YTD")}
                                >
                                    {calculateVariance(actual, budget)}
                                </td>
                            );
                        })()}
                        {(() => {
                            const result = parseIncomeValue(calculateNetOperatingIncome('BudgetFullYearRevised'));
                            return (
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetOperatingIncomeTooltipHtml('BudgetFullYearRevised')}
                                >
                                    {result.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                </td>
                            );
                        })()}
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetOperatingIncomeTooltipHtml('BudgetFullYear')}
                        >
                            {calculateNetOperatingIncome('BudgetFullYear')}
                        </td>
                        {(() => {
                            const forecast = parseIncomeValue(calculateNetOperatingIncome('BudgetFullYearRevised'));
                            const forecastStr = forecast.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                            const budgetStr = calculateNetOperatingIncome('BudgetFullYear');
                            const tooltipHtml = `
                            <table>
                                <thead>
                                    <tr>
                                        <th>Component</th>
                                        <th>Amount (R'm)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style="font-weight:bold;"><td>Forecast Total</td><td>${forecastStr}</td></tr>
                                    <tr style="font-weight:bold;"><td>Budget Full Year</td><td>${budgetStr}</td></tr>
                                </tbody>
                            </table>
                        `;
                            return (
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={tooltipHtml}
                                >
                                    {calculateVariance(forecastStr, budgetStr)}
                                </td>
                            );
                        })()}
                    </tr>
                    <tr>
                        <td>Non operating expenses</td>
                        <td
                            data-tooltip-id="tt"
                            data-tooltip-content={getNonOperatingExTooltipHtml('CurrentMovements')}
                        >
                            {calculateNonOperatingEx('CurrentMovements')}
                        </td>
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getNonOperatingExTooltipHtml('BudgetAmount')}
                        >
                            {calculateNonOperatingEx('BudgetAmount')}
                        </td>
                        {(() => {
                            const actual = calculateNonOperatingEx('CurrentMovements');
                            const budget = calculateNonOperatingEx('BudgetAmount');
                            return (
                                <td style={{ borderRight: '2px solid #57565B' }}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual Month", "Budget Month")}
                                >
                                    {calculateVariance(actual, budget)}
                                </td>
                            );
                        })()}
                        <td
                            data-tooltip-id="tt"
                            data-tooltip-content={getNonOperatingExTooltipHtml('CurrentBal')}
                        >
                            {calculateNonOperatingEx('CurrentBal')}
                        </td>
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getNonOperatingExTooltipHtml('BudgetYearToDate')}
                        >
                            {calculateNonOperatingEx('BudgetYearToDate')}
                        </td>
                        {(() => {
                            const actual = calculateNonOperatingEx('CurrentBal');
                            const budget = calculateNonOperatingEx('BudgetYearToDate');
                            return (
                                <td style={{ borderRight: '2px solid #57565B' }}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual YTD", "Budget YTD")}
                                >
                                    {calculateVariance(actual, budget)}
                                </td>
                            );
                        })()}
                        {(() => {
                            const result = parseIncomeValue(calculateNonOperatingEx('BudgetFullYearRevised'));
                            return (
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNonOperatingExTooltipHtml('BudgetFullYearRevised')}
                                >
                                    {result.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                </td>
                            );
                        })()}
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getNonOperatingExTooltipHtml('BudgetFullYear')}
                        >
                            {calculateNonOperatingEx('BudgetFullYear')}
                        </td>
                        {(() => {
                            const forecast = parseIncomeValue(calculateNonOperatingEx('BudgetFullYearRevised'));
                            const forecastStr = forecast.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                            const budgetStr = calculateNonOperatingEx('BudgetFullYear');
                            const tooltipHtml = `
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Component</th>
                                            <th>Amount (R'm)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style="font-weight:bold;"><td>Forecast Total</td><td>${forecastStr}</td></tr>
                                        <tr style="font-weight:bold;"><td>Budget Full Year</td><td>${budgetStr}</td></tr>
                                    </tbody>
                                </table>
                            `;
                            return (
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={tooltipHtml}
                                >
                                    {calculateVariance(forecastStr, budgetStr)}
                                </td>
                            );
                        })()}
                    </tr>
                    <tr className="subtotal-row bold-row">
                        <td>Net operating income before finance costs</td>
                        <td
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetOperatingIncomeBeforeFinanceTooltipHtml('CurrentMovements')}
                        >
                            {calculateNetOperatingIncomeBeforeFinance('CurrentMovements')}
                        </td>
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetOperatingIncomeBeforeFinanceTooltipHtml('BudgetAmount')}
                        >
                            {calculateNetOperatingIncomeBeforeFinance('BudgetAmount')}
                        </td>
                        {(() => {
                            const actual = calculateNetOperatingIncomeBeforeFinance('CurrentMovements');
                            const budget = calculateNetOperatingIncomeBeforeFinance('BudgetAmount');
                            return (
                                <td style={{ borderRight: '2px solid #57565B' }}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual Month", "Budget Month")}
                                >
                                    {calculateVariance(actual, budget)}
                                </td>
                            );
                        })()}
                        <td
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetOperatingIncomeBeforeFinanceTooltipHtml('CurrentBal')}
                        >
                            {calculateNetOperatingIncomeBeforeFinance('CurrentBal')}
                        </td>
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetOperatingIncomeBeforeFinanceTooltipHtml('BudgetYearToDate')}
                        >
                            {calculateNetOperatingIncomeBeforeFinance('BudgetYearToDate')}
                        </td>
                        {(() => {
                            const actual = calculateNetOperatingIncomeBeforeFinance('CurrentBal');
                            const budget = calculateNetOperatingIncomeBeforeFinance('BudgetYearToDate');
                            return (
                                <td style={{ borderRight: '2px solid #57565B' }}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual YTD", "Budget YTD")}
                                >
                                    {calculateVariance(actual, budget)}
                                </td>
                            );
                        })()}
                        {(() => {
                            const result = parseIncomeValue(calculateNetOperatingIncomeBeforeFinance('BudgetFullYearRevised'));
                            return (
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetOperatingIncomeBeforeFinanceTooltipHtml('BudgetFullYearRevised')}
                                >
                                    {result.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                </td>
                            );
                        })()}
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetOperatingIncomeBeforeFinanceTooltipHtml('BudgetFullYear')}
                        >
                            {calculateNetOperatingIncomeBeforeFinance('BudgetFullYear')}
                        </td>
                        {(() => {
                            const forecast = parseIncomeValue(calculateNetOperatingIncomeBeforeFinance('BudgetFullYearRevised'));
                            const forecastStr = forecast.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                            const budgetStr = calculateNetOperatingIncomeBeforeFinance('BudgetFullYear');
                            const tooltipHtml = `
                            <table>
                                <thead>
                                    <tr>
                                        <th>Component</th>
                                        <th>Amount (R'm)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style="font-weight:bold;"><td>Forecast Total</td><td>${forecastStr}</td></tr>
                                    <tr style="font-weight:bold;"><td>Budget Full Year</td><td>${budgetStr}</td></tr>
                                </tbody>
                            </table>
                        `;
                            return (
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={tooltipHtml}
                                >
                                    {calculateVariance(forecastStr, budgetStr)}
                                </td>
                            );
                        })()}
                    </tr>
                    <tr>
                        <td>Net interest expense</td>
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetInterestExTooltipHtml('CurrentMovements')}
                        >{calculateNetInterestEx('CurrentMovements')}</td>
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetInterestExTooltipHtml('BudgetAmount')}
                                >{calculateNetInterestEx('BudgetAmount')}</td>
                                <td style={{ borderRight: '2px solid #57565B' }}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getVarianceTooltipHtml(
                                        calculateNetInterestEx('CurrentMovements'),
                                        calculateNetInterestEx('BudgetAmount'),
                                        "Actual Month",
                                        "Budget Month"
                                    )}
                                >
                                    {calculateVariance(calculateNetInterestEx('CurrentMovements'), calculateNetInterestEx('BudgetAmount'))}
                                </td>
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetInterestExTooltipHtml('CurrentBal')}
                                >{calculateNetInterestEx('CurrentBal')}</td>
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetInterestExTooltipHtml('BudgetYearToDate')}
                                >{calculateNetInterestEx('BudgetYearToDate')}</td>
                                {(() => {
                                    const actual = calculateNetInterestEx('CurrentBal');
                                    const budget = calculateNetInterestEx('BudgetYearToDate');
                                    return (
                                        <td style={{ borderRight: '2px solid #57565B' }}
                                            data-tooltip-id="tt"
                                            data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual YTD", "Budget YTD")}
                                        >
                                            {calculateVariance(actual, budget)}
                                        </td>
                                    );
                                })()}
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetInterestExTooltipHtml('BudgetFullYearRevised')}
                                >
                                    {
                                        (() => {
                                            const result = parseIncomeValue(calculateNetInterestEx('BudgetFullYearRevised'));
                                            return result.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                                        })()
                                    }
                                </td>
                                <td className="padding-col"
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetInterestExTooltipHtml('BudgetFullYear')}
                                >{calculateNetInterestEx('BudgetFullYear')}</td>
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={(() => {
                                        const forecast = parseIncomeValue(calculateNetInterestEx('BudgetFullYearRevised'));
                                        const forecastStr = forecast.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                                        const budgetStr = calculateNetInterestEx('BudgetFullYear');
                                        return `
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Component</th>
                                                    <th>Amount (R'm)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr style="font-weight:bold;"><td>Forecast Total</td><td>${forecastStr}</td></tr>
                                                <tr style="font-weight:bold;"><td>Budget Full Year</td><td>${budgetStr}</td></tr>
                                            </tbody>
                                        </table>
                                    `;
                                    })()}
                                >
                                    {
                                        (() => {
                                            const forecast = parseIncomeValue(calculateNetInterestEx('BudgetFullYearRevised'));
                                            const forecastStr = forecast.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                                            const budgetStr = calculateNetInterestEx('BudgetFullYear');
                                            return calculateVariance(forecastStr, budgetStr);
                                        })()
                                    }
                                </td>
                    </tr>
                    <tr>
                        <td>Revaluation and tax</td>
                        <td
                            data-tooltip-id="tt"
                            data-tooltip-content={getRevalAndTaxTooltipHtml('CurrentMovements')}
                        >
                            {formatOrDash(calculateRevalAndTax('CurrentMovements'))}
                        </td>
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getRevalAndTaxTooltipHtml('BudgetAmount')}
                        >
                            {formatOrDash(calculateRevalAndTax('BudgetAmount'))}
                        </td>
                        {(() => {
                            const actual = calculateRevalAndTax('CurrentMovements');
                            const budget = calculateRevalAndTax('BudgetAmount');
                            return (
                                <td style={{ borderRight: '2px solid #57565B' }}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual Month", "Budget Month")}
                                >
                                    {calculateVariance(actual, budget)}
                                </td>
                            );
                        })()}
                        <td
                            data-tooltip-id="tt"
                            data-tooltip-content={getRevalAndTaxTooltipHtml('CurrentBal')}
                        >
                            {formatOrDash(calculateRevalAndTax('CurrentBal'))}
                        </td>
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getRevalAndTaxTooltipHtml('BudgetYearToDate')}
                        >
                            {formatOrDash(calculateRevalAndTax('BudgetYearToDate'))}
                        </td>
                        {(() => {
                            const actual = calculateRevalAndTax('CurrentBal');
                            const budget = calculateRevalAndTax('BudgetYearToDate');
                            return (
                                <td style={{ borderRight: '2px solid #57565B' }}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual YTD", "Budget YTD")}
                                >
                                    {calculateVariance(actual, budget)}
                                </td>
                            );
                        })()}
                        {(() => {
                            const result = parseIncomeValue(calculateRevalAndTax('BudgetFullYearRevised'));
                            const formatted = result.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                            return (
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getRevalAndTaxTooltipHtml('BudgetFullYearRevised')}
                                >
                                    {formatOrDash(formatted)}
                                </td>
                            );
                        })()}
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getRevalAndTaxTooltipHtml('BudgetFullYear')}
                        >
                            {formatOrDash(calculateRevalAndTax('BudgetFullYear'))}
                        </td>
                        {(() => {
                            const forecast = parseIncomeValue(calculateRevalAndTax('BudgetFullYearRevised'));
                            const forecastStr = forecast.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                            const budgetStr = calculateRevalAndTax('BudgetFullYear');
                            return (
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={(() => {
                                        return `
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Component</th>
                                                    <th>Amount (R'm)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr style="font-weight:bold;"><td>Forecast Total</td><td>${forecastStr}</td></tr>
                                                <tr style="font-weight:bold;"><td>Budget Full Year</td><td>${budgetStr}</td></tr>
                                            </tbody>
                                        </table>
                                    `;
                                    })()}
                                >
                                    {calculateVariance(forecastStr, budgetStr)}
                                </td>
                            );
                        })()}
                    </tr>
            
                    <tr className="total-row">
                        <td>Net income</td>
                        <td
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetIncomeTooltipHtml('CurrentMovements')}
                        >
                            {calculateNetIncome('CurrentMovements')}
                        </td>
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetIncomeTooltipHtml('BudgetAmount')}
                        >
                            {calculateNetIncome('BudgetAmount')}
                        </td>
                        {(() => {
                            const actual = calculateNetIncome('CurrentMovements');
                            const budget = calculateNetIncome('BudgetAmount');
                            return (
                                <td style={{ borderRight: '2px solid #57565B' }}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual Month", "Budget Month")}
                                >
                                    {calculateVariance(actual, budget)}
                                </td>
                            );
                        })()}
                        <td
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetIncomeTooltipHtml('CurrentBal')}
                        >
                            {calculateNetIncome('CurrentBal')}
                        </td>
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetIncomeTooltipHtml('BudgetYearToDate')}
                        >
                            {calculateNetIncome('BudgetYearToDate')}
                        </td>
                        {(() => {
                            const actual = calculateNetIncome('CurrentBal');
                            const budget = calculateNetIncome('BudgetYearToDate');
                            return (
                                <td style={{ borderRight: '2px solid #57565B' }}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getVarianceTooltipHtml(actual, budget, "Actual YTD", "Budget YTD")}
                                >
                                    {calculateVariance(actual, budget)}
                                </td>
                            );
                        })()}
                        {(() => {
                            const result = parseIncomeValue(calculateNetIncome('BudgetFullYearRevised'));
                            return (
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={getNetIncomeTooltipHtml('BudgetFullYearRevised')}
                                >
                                    {result.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                </td>
                            );
                        })()}
                        <td className="padding-col"
                            data-tooltip-id="tt"
                            data-tooltip-content={getNetIncomeTooltipHtml('BudgetFullYear')}
                        >
                            {calculateNetIncome('BudgetFullYear')}
                        </td>
                        {(() => {
                            const forecast = parseIncomeValue(calculateNetIncome('BudgetFullYearRevised'));
                            const forecastStr = forecast.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                            const budgetStr = calculateNetIncome('BudgetFullYear');
                            return (
                                <td
                                    data-tooltip-id="tt"
                                    data-tooltip-content={(() => {
                                        return `
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Component</th>
                                                    <th>Amount (R'm)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr style="font-weight:bold;"><td>Forecast Total</td><td>${forecastStr}</td></tr>
                                                <tr style="font-weight:bold;"><td>Budget Full Year</td><td>${budgetStr}</td></tr>
                                            </tbody>
                                        </table>
                                    `;
                                    })()}
                                >
                                    {calculateVariance(forecastStr, budgetStr)}
                                </td>
                            );
                        })()}
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

CondensedIncomeStatementTable.propTypes = {
    condensedIncomeData: PropTypes.arrayOf(PropTypes.object).isRequired,
    period: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    totalArea: PropTypes.number.isRequired,
    propertyData: PropTypes.object.isRequired
};

export default CondensedIncomeStatementTable;
